
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, signal, WritableSignal, inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { SnackNotificationsDialogService } from '@ui-lib/services';
import { catchError, EMPTY, map, noop, Observable, of, Subject, Subscription, takeUntil } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { StaffUsersService } from './../../../../../api/staff-users/staff-users.service';

interface ClinicianFilesResponse {
    signature_image_url?: string;
    clinicianVideos?: ClinicianVideo[];
}

interface ClinicianVideo {
    id: number;
    video_type: VideoType;
    url: string;
}

export enum VideoType {
    APPROVED ='approved',
    NOT_TREATED = 'not_treated'
}
@Component({
    selector: 'prescriber-files',
    imports: [ReactiveFormsModule, FormsModule],
    templateUrl: './prescriber-files.component.html',
    styleUrl: './prescriber-files.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PrescriberFilesComponent implements OnInit, OnDestroy {
    private staffUserApiService = inject(StaffUsersService);
    private snackNotificationsService = inject(SnackNotificationsDialogService);

    @Input({ required: true }) userId!: number;

    signatureExists: WritableSignal<boolean> = signal(false);
    approvedVideoExists: WritableSignal<boolean> = signal(false);
    notApprovedVideoExists: WritableSignal<boolean> = signal(false);

    approvedVideoSrc: string | null = null;
    notApprovedVideoSrc: string | null = null;
    signatureImgSrc: WritableSignal<string> = signal('');
    readonly MAX_SIZE = 50 * 1024; // 50 KB
    signatureImageSubmitClick$$ = new Subject<number>();
    approvedVideoSubmitClick$$ = new Subject<number>();
    notApprovedVideoSubmitClick$$ = new Subject<number>();
    uploadForm = signal<FormGroup>(
        new FormGroup({
            image: new FormControl(null, [Validators.required]),
            approvedVideo: new FormControl(null, [Validators.required]),
            notApprovedVideo: new FormControl(null, [Validators.required]),
        }),
    );
    subscriptions = new Subscription();

    ngOnInit(): void {
        this.loadExistingFiles();
        const statusError$ = of('').pipe(
            tap(() => this.snackNotificationsService.openErrorMessage('No file selected or form is invalid.')),
        );

        this.subscriptions.add(
            this.signatureImageSubmitClick$$
                .pipe(
                    switchMap(userId =>
                        this.uploadForm().controls['image'].invalid ? statusError$ : this.uploadSignatureImage(userId),
                    ),
                )
                .subscribe(noop),
        );

        this.subscriptions.add(
            this.approvedVideoSubmitClick$$
                .pipe(
                    switchMap(userId =>
                        this.uploadForm().controls['approvedVideo'].invalid ? statusError$ : this.uploadVideo(userId, VideoType.APPROVED, this.uploadForm().controls['approvedVideo']),
                    ),
                )
                .subscribe(noop),
        );

        this.subscriptions.add(
            this.notApprovedVideoSubmitClick$$
                .pipe(
                    switchMap(userId =>
                        this.uploadForm().controls['notApprovedVideo'].invalid ? statusError$ : this.uploadVideo(userId, VideoType.NOT_TREATED, this.uploadForm().controls['notApprovedVideo']),
                    ),
                )
                .subscribe(noop),
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    private loadExistingFiles(): void {
        this.staffUserApiService.getStaffUser(this.userId)
          .pipe(
            tap((response:  ClinicianFilesResponse) => {
                const clinicianVideos = response?.clinicianVideos ?? [];
                this.signatureExists.set(!!response.signature_image_url);
                this.approvedVideoExists.set(clinicianVideos.some(video => video.video_type === VideoType.APPROVED));
                this.notApprovedVideoExists.set(clinicianVideos.some(video => video.video_type === VideoType.NOT_TREATED));
            }),
            catchError((err) => {
                console.error('Error fetching clinician files:', err);
                return EMPTY;
            })
          )
          .subscribe();
    }


    private get onSignatureImageFileChangeErrors() {
        return [
            {
                condition: (file: File) => file.type !== 'image/png',
                message: 'Please upload a valid PNG file.',
            },
            {
                condition: (file: File) => file.size > this.MAX_SIZE,
                message: 'The file is too large. Maximum size is 50 KB.',
            },
        ];
    }

    onSignatureImageFileChange(event: Event, imageFileInput: HTMLInputElement): void {
        const input = event.target as HTMLInputElement;
        const imageChangeErrors = this.onSignatureImageFileChangeErrors;
        if (input.files && input.files.length > 0) {
            const file = input.files[0];

            const error = imageChangeErrors.find(errObj => errObj.condition(file));
            if (error) {
                this.snackNotificationsService.openErrorMessage(error.message);
                imageFileInput.value = '';
                this.uploadForm().controls['image'].setValue(undefined);
                this.signatureImgSrc.update(() => '');
                return;
            }

            this.uploadForm().controls['image'].patchValue(file);
            const reader = new FileReader();
            reader.onload = (e: any) => {
                this.signatureImgSrc.update(() => e.target?.result as string);
            };
            reader.readAsDataURL(file);
        }
    }

    private handleVideoFileChange(event: Event, videoFileInput: HTMLInputElement, videoFormControl: AbstractControl): File | null  {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            const file = input.files[0];
            if (!file.type.startsWith('video/')) {
                this.snackNotificationsService.openErrorMessage('Please upload a valid video file.');
                videoFileInput.value = '';
               videoFormControl.setValue(undefined);
                return null;
            }

            videoFormControl.patchValue(file);
            return file;
        }
        return null;
    }

    onApprovedVideoFileChange(event: Event, approvedVideoFileInput: HTMLInputElement, videoFormControl: AbstractControl): void {
        const approvedVideoFile = this.handleVideoFileChange(event, approvedVideoFileInput, videoFormControl);
        if (approvedVideoFile) {
            this.approvedVideoSrc = URL.createObjectURL(approvedVideoFile);
        }
    }

    onNotApprovedVideoFileChange(event: Event, notApprovedVideoFileInput: HTMLInputElement, videoFormControl: AbstractControl): void {
        const notApprovedVideoFile = this.handleVideoFileChange(event, notApprovedVideoFileInput, videoFormControl);
        if (notApprovedVideoFile) {
            this.notApprovedVideoSrc = URL.createObjectURL(notApprovedVideoFile);
        }
    }

    signatureImageSubmitClick(userId: number) {
        this.signatureImageSubmitClick$$.next(userId);
    }

    approvedVideoSubmitClick(userId: number) {
        this.approvedVideoSubmitClick$$.next(userId);
    }
    notApprovedVideoSubmitClick(userId: number) {
        this.notApprovedVideoSubmitClick$$.next(userId);
    }

    uploadSignatureImage(userId: number): Observable<void | Object> {
        const formData = new FormData();
        formData.append('signature', this.uploadForm().controls['image'].value as File);
        return this.staffUserApiService.uploadSignatureFile(userId, formData).pipe(
            catchError(err => {
                this.snackNotificationsService.openErrorMessage('Image file upload failed.');
                console.error('Error:', err);
                return EMPTY;
            }),
            tap(() => this.snackNotificationsService.openSuccessMessage('Image file submitted successfully.')),
        );
    }

    uploadVideo(userId: number, videoType: VideoType, videoFormControl: AbstractControl): Observable<void | Object> {
        const formData = new FormData();
        formData.append('file', videoFormControl.value as File);
        formData.append('type', videoType);
        return this.staffUserApiService.uploadVideoFile(userId, formData).pipe(
            catchError(err => {
                this.snackNotificationsService.openErrorMessage('Video file upload failed.');
                console.error('Error:', err);
                return EMPTY;
            }),
            tap(() => this.snackNotificationsService.openSuccessMessage('Video file submitted successfully.')),
        );
    }
}
